import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import DynamicPatientForm from '../components/DynamicPatientForm';

import API_BASE_URL from '../config/api';
const API_URL = API_BASE_URL;

function PatientFormPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [patientData, setPatientData] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [redFlagResult, setRedFlagResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadPatientForm();
  }, [token]);

  const loadPatientForm = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/patients/form/${token}`);
      setPatientData(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error loading patient form:', err);
      if (err.response?.status === 410) {
        setError('This form link has expired. Please contact the clinic at cambridge@angsm.co.nz or 07 957 6064 to request a new link.');
      } else if (err.response?.status === 400) {
        setError('This form has already been submitted. If you believe this is an error, please contact the clinic.');
      } else {
        setError('Unable to load form. Please check your link or contact the clinic at cambridge@angsm.co.nz or 07 957 6064.');
      }
      setLoading(false);
    }
  };

  const handleSubmit = async (formData) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/patients/submit/${token}`, {
        demographics: formData,
        clinicalData: formData
      });

      setSubmitted(true);

      if (response.data.redFlags) {
        setRedFlagResult(response.data.redFlags);
      }
    } catch (err) {
      console.error('Form submission failed');

      // Show more detailed error
      const errorMessage = err.response?.data?.error || err.message || 'Unknown error';
      alert(`Error submitting form: ${errorMessage}\n\nPlease try again or contact support.`);
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh', 
        background: '#f3f4f6',
        padding: '20px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '48px', 
            height: '48px', 
            border: '4px solid #e5e7eb', 
            borderTopColor: '#2563eb', 
            borderRadius: '50%', 
            animation: 'spin 0.8s linear infinite', 
            margin: '0 auto 16px' 
          }} />
          <p style={{ color: '#6b7280', fontSize: '16px' }}>Loading your form...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh', 
        background: '#f3f4f6', 
        padding: '16px' 
      }}>
        <div style={{ 
          background: 'white', 
          padding: '24px', 
          borderRadius: '12px', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)', 
          maxWidth: '500px', 
          width: '100%',
          textAlign: 'center' 
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <h2 style={{ color: '#dc2626', marginBottom: '12px', fontSize: '20px' }}>Error Loading Form</h2>
          <p style={{ color: '#6b7280', fontSize: '16px', lineHeight: '1.5' }}>{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
        padding: '20px 12px' 
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          
          {redFlagResult && redFlagResult.hasRedFlags && (
            <div style={{ 
              background: redFlagResult.level === 'critical' ? '#fee2e2' : '#fef3c7', 
              border: `3px solid ${redFlagResult.level === 'critical' ? '#dc2626' : '#f59e0b'}`, 
              borderRadius: '12px', 
              padding: '20px', 
              marginBottom: '20px', 
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)' 
            }}>
              <h2 style={{ 
                marginTop: 0, 
                fontSize: '22px', 
                color: redFlagResult.level === 'critical' ? '#dc2626' : '#f59e0b',
                lineHeight: '1.3'
              }}>
                {redFlagResult.level === 'critical' ? '🚨' : '⚠️'} Urgent Medical Attention Required
              </h2>
              <p style={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '16px' }}>
                Based on your responses, you have reported symptoms that may indicate a serious medical condition.
                Our clinical team has been immediately notified and will contact you shortly.
              </p>
              {redFlagResult.level === 'critical' && (
                <div style={{ 
                  background: '#dc2626', 
                  color: 'white', 
                  padding: '16px', 
                  borderRadius: '8px',
                  fontSize: '15px',
                  lineHeight: '1.5'
                }}>
                  <strong>If your symptoms worsen or you feel unwell, please go to the Emergency Department immediately.</strong>
                </div>
              )}
            </div>
          )}

          <div style={{ 
            background: 'white', 
            borderRadius: '12px', 
            padding: '24px 20px', 
            boxShadow: '0 10px 30px rgba(0,0,0,0.2)', 
            textAlign: 'center' 
          }}>
            <div style={{ fontSize: '56px', marginBottom: '16px' }}>✅</div>
            <h1 style={{ marginTop: 0, color: '#1f2937', fontSize: '24px', lineHeight: '1.3' }}>
              Form Submitted Successfully!
            </h1>
            <p style={{ fontSize: '16px', color: '#6b7280', lineHeight: '1.6' }}>
              Thank you for completing your medical history form.
            </p>
            
            <div style={{ 
              background: '#f3f4f6', 
              borderRadius: '8px', 
              padding: '20px', 
              marginTop: '24px', 
              textAlign: 'left' 
            }}>
              <h3 style={{ marginTop: 0, fontSize: '18px' }}>What happens next:</h3>
              <ul style={{ lineHeight: '1.8', paddingLeft: '20px', margin: 0, fontSize: '15px' }}>
                <li>Our clinical team will review your information</li>
                <li>We will contact you within 24-48 hours to schedule your appointment</li>
                <li>Please ensure your phone is available for our call</li>
                {redFlagResult && redFlagResult.hasRedFlags && (
                  <li style={{ color: '#dc2626', fontWeight: 'bold' }}>
                    Due to urgent symptoms, you will be contacted today
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
      padding: '16px 8px',
      boxSizing: 'border-box'
    }}>
      {/* Add viewport meta tag reminder - this should be in index.html */}
      <style>{`
        @media (max-width: 768px) {
          .form-container {
            padding: 16px !important;
          }
          .form-header {
            padding: 20px 16px !important;
          }
          .form-header h1 {
            font-size: 22px !important;
          }
        }
        
        /* Prevent iOS zoom on input focus */
        input, select, textarea {
          font-size: 16px !important;
        }
        
        /* Better touch targets */
        button {
          min-height: 44px;
        }
        
        /* Smooth scrolling */
        html {
          scroll-behavior: smooth;
        }
      `}</style>
      
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        
        <div 
          className="form-header"
          style={{ 
            background: 'white', 
            borderRadius: '12px', 
            padding: '20px', 
            marginBottom: '16px', 
            boxShadow: '0 10px 30px rgba(0,0,0,0.2)' 
          }}
        >
          <h1 style={{ margin: '0 0 8px 0', color: '#1f2937', fontSize: '24px', lineHeight: '1.3' }}>
            Patient Medical History Form
          </h1>
          <p style={{ margin: 0, color: '#6b7280', fontSize: '15px', lineHeight: '1.5' }}>
            Please complete all sections accurately. Your information is secure and confidential.
          </p>
        </div>

        <div 
          className="form-container"
          style={{ 
            background: 'white', 
            borderRadius: '12px', 
            padding: '20px', 
            boxShadow: '0 10px 30px rgba(0,0,0,0.2)' 
          }}
        >
          <DynamicPatientForm
            injuryType={patientData?.form?.template || patientData?.referralInfo?.injuryType || 'KNEE'}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            initialData={{ ...patientData?.patient, ...patientData?.form?.demographics, ...patientData?.form?.clinicalData }}
          />
        </div>
      </div>
    </div>
  );
}

export default PatientFormPage;
